import { HashMap } from 'shared/types/commonView';
import { isDefinedValue } from './value';

function objHasKey<T>(obj: T, key: PropertyKey): key is keyof T {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

const deepFind = <T>(obj: any, path: string): T[] => {
  if (!path) {
    return [];
  }
  const paths = path.split('.');
  const result: T[] = [];
  let current = obj;

  paths.forEach((path, index) => {
    if (!current) {
      return;
    }
    const pathFound = current?.[path] !== undefined;
    if (Array.isArray(current)) {
      current.forEach((el) => {
        const foundInObjectFromArray: T[] = deepFind(el, path);
        if (foundInObjectFromArray) {
          foundInObjectFromArray.forEach((elem) => result.push(elem));
        }
      });
    }
    const isLastIteration = index + 1 === paths.length;
    if (pathFound && isLastIteration) {
      result.push(current[path]);
    }
    current = current[path];
  });
  return result;
};

const iterate = <T>(obj: HashMap<any>, key: string): T | undefined => {
  let result;

  for (const property in obj) {
    if (objHasKey(obj, property)) {
      if (property === key) {
        return obj[key]; // returns the value
      } else if (typeof obj[property] === 'object') {
        // in case it is an object
        result = iterate(obj[property], key);

        if (typeof result !== 'undefined') {
          return result as T;
        }
      }
    }
  }
};

const isObject = <T>(value: unknown): value is T => {
  return typeof value === 'object' && Boolean(value);
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.prototype.toString.call(value) === '[object Object]'
  );
};

function isPlainObjectWithValues(
  value: unknown,
  requiredKeys: string[]
): value is Record<string, unknown> {
  if (!isPlainObject(value)) return false;
  return requiredKeys.every(
    (key) => objHasKey(value, key) && isDefinedValue(value[key])
  );
}

/** A record with the same keys, each value mapped (e.g. a registry of contracts into clients). */
function mapRecordValues<TKey extends string, TValue, TResult>(
  record: Record<TKey, TValue>,
  mapValue: (value: TValue, key: TKey) => TResult
): Record<TKey, TResult> {
  return Object.fromEntries(
    (Object.entries(record) as [TKey, TValue][]).map(([key, value]) => [key, mapValue(value, key)])
  ) as Record<TKey, TResult>;
}

export {
  deepFind,
  iterate,
  objHasKey,
  isObject,
  isPlainObject,
  isPlainObjectWithValues,
  mapRecordValues,
};
